/**
 * Spreadsheet-style keyboard navigation over the logical grid (see
 * docs/calendar-drop-in-replacement-plan.md "Keyboard navigation" and the
 * multi-select & group-move requirement).
 *
 * Cross-engine keyboard contract: molecules/Calendar/hotkeys/keyboardSpec.ts.
 * When keys are added or refined here, update ./keyboardConformance.ts — it is
 * this engine's authoritative parity record against the production calendar.
 *
 *  - Arrows move the cursor; Shift+Arrows extend an Excel-style range.
 *  - Enter selects the event under the cursor; the pointer side XORs events
 *    into the multi-selection with Ctrl/Cmd+Click and marquee-drags ranges.
 *  - "v" (verschieben) picks the selection up, arrows/pointer preview the
 *    placement, "v"/Enter places, Escape cancels.
 *
 * All lookups run against the in-memory indexes — no DOM scanning. The
 * keydown handler mutates only the interaction mini store, so a navigation
 * step re-renders exactly: the overlays, the time chip and up to two header
 * cells.
 */
import { useCallback, useMemo, useRef } from "react";
import {
  EMPTY_ID_SET,
  MCCellPos,
  MCEvent,
  MCEventMove,
  MCGridConfig,
  MCInteractionState,
  MCResource,
} from "./model/types";
import { MCEventIndex, eventAtCell } from "./model/eventIndex";
import { clampSlot, dateToMinutes, yForMin } from "./model/gridGeometry";
import { clampDelta, eventIdsInRange, movableEvents, planMove } from "./model/selection";
import { MiniStore } from "./model/miniStore";

interface GridKeyboardOptions {
  cfg: MCGridConfig;
  store: MiniStore<MCInteractionState>;
  indexRef: React.RefObject<MCEventIndex>;
  resourcesRef: React.RefObject<MCResource[]>;
  scrollerRef: React.RefObject<HTMLDivElement>;
  colWidthRef: React.RefObject<number>;
  onEventSelect?: (event: MCEvent) => void;
  /**
   * Keyboard cursor entered/left an event (fires once per change, with the
   * event or null). Consumers debounce it themselves — e.g. the bridge opens
   * the patient overview after the cursor rests on an appointment.
   */
  onEventCursor?: (event: MCEvent | null) => void;
  /** Commit a resolved group move (bridge persists it). */
  onMoveCommit?: (moves: MCEventMove[]) => void | Promise<unknown>;
  /**
   * "v" on a cell with nothing movable under the cursor (spec: umziehen place
   * step). The bridge places the most recent pinboard item there; returns
   * true when it consumed the key.
   */
  onEmptyCellV?: (cell: MCCellPos) => boolean;
}

/** Shared "verschieben" actions used by both keyboard and pointer flows. */
export interface MoveActions {
  /** Pick up the current selection; false when nothing movable is selected. */
  pickUp: () => boolean;
  /** Place at the current delta; false when the placement is invalid. */
  place: () => boolean;
  cancel: () => void;
  /** Set the delta from the grab cell to the given cell (pointer preview). */
  previewAtCell: (cell: MCCellPos) => void;
}

const isTypingContext = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(
    target.closest("input, textarea, select, [contenteditable='true'], .MuiInputBase-root"),
  );
};

export const useGridKeyboard = ({
  cfg,
  store,
  indexRef,
  resourcesRef,
  scrollerRef,
  colWidthRef,
  onEventSelect,
  onEventCursor,
  onMoveCommit,
  onEmptyCellV,
}: GridKeyboardOptions) => {
  /** Row remembered when moving across an event horizontally (plan §keyboard) */
  const rememberedRowRef = useRef<number | null>(null);
  /** Last event id reported via onEventCursor (dedupes per-slot steps inside one event). */
  const cursorEventIdRef = useRef<string | null>(null);

  const ensureVisible = useCallback(
    (pos: MCCellPos) => {
      const scroller = scrollerRef.current;
      const colWidth = colWidthRef.current || 1;
      if (!scroller) return;

      const cellX = cfg.gutterWidthPx + pos.resourceIndex * colWidth;
      const cellY = cfg.headerHeightPx + yForMin(cfg, pos.slotMin);
      const padding = cfg.slotHeightPx * 2;

      let { scrollLeft, scrollTop } = scroller;
      const viewLeft = scrollLeft + cfg.gutterWidthPx;
      const viewRight = scrollLeft + scroller.clientWidth;
      const viewTop = scrollTop + cfg.headerHeightPx;
      const viewBottom = scrollTop + scroller.clientHeight;

      if (cellX < viewLeft) scrollLeft = cellX - cfg.gutterWidthPx;
      else if (cellX + colWidth > viewRight) scrollLeft = cellX + colWidth - scroller.clientWidth;

      if (cellY < viewTop + padding) scrollTop = cellY - cfg.headerHeightPx - padding;
      else if (cellY + cfg.slotHeightPx > viewBottom - padding)
        scrollTop = cellY + cfg.slotHeightPx - scroller.clientHeight + padding;

      if (scrollLeft !== scroller.scrollLeft || scrollTop !== scroller.scrollTop) {
        scroller.scrollTo({ left: Math.max(0, scrollLeft), top: Math.max(0, scrollTop) });
      }
    },
    [cfg, scrollerRef, colWidthRef],
  );

  const commitFocus = useCallback(
    (pos: MCCellPos) => {
      const resources = resourcesRef.current ?? [];
      const index = indexRef.current;
      const resource = resources[pos.resourceIndex];
      const overlapping =
        index && resource ? eventAtCell(index, resource.id, pos.slotMin) : null;
      store.set({ focus: pos, focusedEventId: overlapping?.id ?? null });
      if (cursorEventIdRef.current !== (overlapping?.id ?? null)) {
        cursorEventIdRef.current = overlapping?.id ?? null;
        onEventCursor?.(overlapping);
      }
      ensureVisible(pos);
    },
    [store, indexRef, resourcesRef, ensureVisible, onEventCursor],
  );

  /** First therapist column. */
  const defaultPos = useCallback((): MCCellPos | null => {
    const resources = resourcesRef.current ?? [];
    const first = resources[0];
    if (!first) return null;
    return {
      resourceIndex: first.index,
      slotMin: clampSlot(cfg, dateToMinutes(new Date())),
    };
  }, [cfg, resourcesRef]);

  const moveVertical = useCallback(
    (pos: MCCellPos, direction: 1 | -1, stepMin: number): MCCellPos => {
      const resources = resourcesRef.current ?? [];
      const index = indexRef.current;
      const resource = resources[pos.resourceIndex];
      let next = pos.slotMin;

      const current = index && resource ? eventAtCell(index, resource.id, pos.slotMin) : null;
      if (current && stepMin === cfg.slotMin) {
        // On an event: jump past its boundary (first slot after / before it).
        next = direction === 1 ? current.endMin : current.startMin - cfg.slotMin;
      } else {
        next = pos.slotMin + direction * stepMin;
      }
      return { ...pos, slotMin: clampSlot(cfg, next) };
    },
    [cfg, indexRef, resourcesRef],
  );

  const moveHorizontal = useCallback(
    (pos: MCCellPos, direction: 1 | -1): MCCellPos => {
      const resources = resourcesRef.current ?? [];
      const i = pos.resourceIndex + direction;
      if (i < 0 || i >= resources.length) return pos;
      const row = rememberedRowRef.current ?? pos.slotMin;
      return { resourceIndex: i, slotMin: row };
    },
    [resourcesRef],
  );

  // ── Range selection (Shift+Arrows / pointer marquee share this) ──────────
  const applyRange = useCallback(
    (anchor: MCCellPos, head: MCCellPos) => {
      const index = indexRef.current;
      const resources = resourcesRef.current ?? [];
      const range = { anchor, head };
      const ids = index ? eventIdsInRange(index, resources, range, cfg) : new Set<string>();
      store.set({
        range,
        multiSelectedIds: ids,
        focus: head,
        focusedEventId: null,
      });
    },
    [cfg, store, indexRef, resourcesRef],
  );

  const extendRange = useCallback(
    (dCols: number, dSlots: number) => {
      const state = store.get();
      const resources = resourcesRef.current ?? [];
      const origin = state.range?.anchor ?? state.focus ?? defaultPos();
      if (!origin) return;
      const head = state.range?.head ?? state.focus ?? origin;
      const next: MCCellPos = {
        resourceIndex: Math.min(
          Math.max(head.resourceIndex + dCols, 0),
          Math.max(resources.length - 1, 0),
        ),
        slotMin: clampSlot(cfg, head.slotMin + dSlots * cfg.slotMin),
      };
      applyRange(origin, next);
      ensureVisible(next);
    },
    [cfg, store, resourcesRef, defaultPos, applyRange, ensureVisible],
  );

  // ── "Verschieben" (pick up / preview / place) ────────────────────────
  const pickUp = useCallback((): boolean => {
    const state = store.get();
    const index = indexRef.current;
    if (!index || state.move) return false;

    let ids: string[] = [];
    if (state.multiSelectedIds.size > 0) ids = [...state.multiSelectedIds];
    else if (state.selectedEventId) ids = [state.selectedEventId];
    else if (state.focusedEventId) ids = [state.focusedEventId];

    const movable = movableEvents(index, ids);
    if (movable.length === 0) return false;

    const resources = resourcesRef.current ?? [];
    const firstCol = resources.find((r) => r.id === movable[0].resourceId)?.index ?? 0;
    const grabCell: MCCellPos =
      state.focus ?? { resourceIndex: firstCol, slotMin: movable[0].startMin };

    store.set({
      move: {
        // Captured snapshot — the whole transaction derives from these
        // objects, so index rebuilds mid-move can never abort it (see
        // MCMoveState.sources).
        sources: movable,
        grabCell,
        deltaCols: 0,
        deltaSlots: 0,
      },
      multiSelectedIds: new Set(movable.map((e) => e.id)),
      range: null,
      focusedEventId: null,
    });
    return true;
  }, [store, indexRef, resourcesRef]);

  const place = useCallback((): boolean => {
    const state = store.get();
    const move = state.move;
    const index = indexRef.current;
    if (!move || !index) return false;
    const resources = resourcesRef.current ?? [];
    const plan = planMove(index, resources, move.sources, move.deltaCols, move.deltaSlots, cfg);
    if (!plan.valid) return false;

    store.set({ move: null });
    if (move.deltaCols !== 0 || move.deltaSlots !== 0) {
      onMoveCommit?.(plan.moves);
    }
    return true;
  }, [cfg, store, indexRef, resourcesRef, onMoveCommit]);

  const cancel = useCallback(() => {
    if (store.get().move) store.set({ move: null });
  }, [store]);

  const nudgeMove = useCallback(
    (dCols: number, dSlots: number) => {
      const state = store.get();
      const move = state.move;
      const index = indexRef.current;
      if (!move || !index) return;
      const resources = resourcesRef.current ?? [];
      const next = clampDelta(
        resources,
        move.sources,
        move.deltaCols + dCols,
        move.deltaSlots + dSlots,
        cfg,
      );
      store.set({ move: { ...move, ...next } });

      // Keep the ghost anchor in view.
      const first = move.sources[0];
      const firstCol = first
        ? resources.find((r) => r.id === first.resourceId)?.index
        : undefined;
      if (first && firstCol != null) {
        ensureVisible({
          resourceIndex: firstCol + next.deltaCols,
          slotMin: clampSlot(cfg, first.startMin + next.deltaSlots * cfg.slotMin),
        });
      }
    },
    [cfg, store, indexRef, resourcesRef, ensureVisible],
  );

  const previewAtCell = useCallback(
    (cell: MCCellPos) => {
      const state = store.get();
      const move = state.move;
      const index = indexRef.current;
      if (!move || !index) return;
      const resources = resourcesRef.current ?? [];
      const next = clampDelta(
        resources,
        move.sources,
        cell.resourceIndex - move.grabCell.resourceIndex,
        Math.round((cell.slotMin - move.grabCell.slotMin) / cfg.slotMin),
        cfg,
      );
      if (next.deltaCols !== move.deltaCols || next.deltaSlots !== move.deltaSlots) {
        store.set({ move: { ...move, ...next } });
      }
    },
    [cfg, store, indexRef, resourcesRef],
  );

  const moveActions: MoveActions = useMemo(
    () => ({ pickUp, place, cancel, previewAtCell }),
    [pickUp, place, cancel, previewAtCell],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isTypingContext(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const state = store.get();
      const index = indexRef.current;
      const resources = resourcesRef.current ?? [];

      // Normalize movement keys: arrows AND the ijkl triangle (i up, k down,
      // j left, l right — spec: cellMove). Shift+i / Shift+k jump 10 rows
      // (spec: cellJump); shifted J/L are the DATE paging combos owned by the
      // central hotkey provider, so they must fall through untouched here.
      const lower = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      type Dir = "up" | "down" | "left" | "right";
      let dir: Dir | null = null;
      let vStep = 1; // vertical step in slots
      if (e.key === "ArrowUp") dir = "up";
      else if (e.key === "ArrowDown") dir = "down";
      else if (e.key === "ArrowLeft") dir = "left";
      else if (e.key === "ArrowRight") dir = "right";
      else if (lower === "i") {
        dir = "up";
        vStep = e.shiftKey ? 10 : 1;
      } else if (lower === "k") {
        dir = "down";
        vStep = e.shiftKey ? 10 : 1;
      } else if (lower === "j" && !e.shiftKey) dir = "left";
      else if (lower === "l" && !e.shiftKey) dir = "right";

      // ── Move mode: arrows/ijkl nudge (Shift+i/k ×10), v/Enter place, Escape cancels ──
      if (state.move) {
        if (dir) {
          e.preventDefault();
          if (dir === "down") nudgeMove(0, vStep);
          else if (dir === "up") nudgeMove(0, -vStep);
          else if (dir === "right") nudgeMove(1, 0);
          else nudgeMove(-1, 0);
          return;
        }
        switch (e.key) {
          case "PageDown":
            e.preventDefault();
            nudgeMove(0, 60 / cfg.slotMin);
            return;
          case "PageUp":
            e.preventDefault();
            nudgeMove(0, -60 / cfg.slotMin);
            return;
          case "v":
          case "V":
          case "Enter":
            e.preventDefault();
            e.stopPropagation();
            place();
            return;
          case "Escape":
            // Consume: this Escape ends the move only — the shared shelf
            // chain (EventShelf) must not ALSO clear the pinboard.
            e.preventDefault();
            e.stopPropagation();
            cancel();
            return;
          default:
            return;
        }
      }

      const withFocus = (fn: (pos: MCCellPos) => void) => {
        const pos = state.focus ?? defaultPos();
        if (!pos) return;
        e.preventDefault();
        fn(pos);
      };

      /** Plain navigation collapses any range/multi selection (Excel). */
      const collapseSelection = () => {
        if (state.range || state.multiSelectedIds.size > 0) {
          store.set({ range: null, multiSelectedIds: EMPTY_ID_SET });
        }
      };

      // Shift+Arrows extend the Excel-style range (engine extension — stays on
      // the ARROW keys only; shifted i/k are the cellJump combos above).
      if (dir && e.shiftKey && e.key.startsWith("Arrow")) {
        e.preventDefault();
        if (dir === "down") extendRange(0, 1);
        else if (dir === "up") extendRange(0, -1);
        else if (dir === "right") extendRange(1, 0);
        else extendRange(-1, 0);
        return;
      }

      if (dir === "up" || dir === "down") {
        const direction = dir === "down" ? 1 : -1;
        withFocus((pos) => {
          rememberedRowRef.current = null;
          collapseSelection();
          // moveVertical applies the jump-over-event behavior only for single
          // steps; multi-row jumps (Shift+i/k) advance plainly by vStep slots.
          commitFocus(state.focus ? moveVertical(pos, direction, cfg.slotMin * vStep) : pos);
        });
        return;
      }
      if (dir === "left" || dir === "right") {
        const direction = dir === "right" ? 1 : -1;
        withFocus((pos) => {
          // Preserve the row used to enter an event when crossing columns.
          if (rememberedRowRef.current == null) rememberedRowRef.current = pos.slotMin;
          collapseSelection();
          commitFocus(state.focus ? moveHorizontal(pos, direction) : pos);
        });
        return;
      }

      switch (e.key) {
        case "PageDown":
        case "PageUp": {
          const direction = e.key === "PageDown" ? 1 : -1;
          if (e.shiftKey) {
            e.preventDefault();
            extendRange(0, direction * (60 / cfg.slotMin));
            return;
          }
          withFocus((pos) => {
            rememberedRowRef.current = null;
            collapseSelection();
            commitFocus({ ...pos, slotMin: clampSlot(cfg, pos.slotMin + direction * 60) });
          });
          break;
        }
        case "Home":
          withFocus((pos) => {
            collapseSelection();
            commitFocus({ ...pos, slotMin: cfg.dayStartMin });
          });
          break;
        case "End":
          withFocus((pos) => {
            collapseSelection();
            commitFocus({ ...pos, slotMin: cfg.dayEndMin - cfg.slotMin });
          });
          break;
        case "v":
        case "V": {
          if (e.shiftKey) return; // Shift+V unassigned — leave to the provider
          e.preventDefault();
          if (pickUp()) return;
          // Nothing movable under the cursor → umziehen place step: let the
          // bridge plant the most recent pinboard item at the focused cell.
          const pos = state.focus ?? defaultPos();
          if (pos) onEmptyCellV?.(pos);
          break;
        }
        case "Enter": {
          const pos = state.focus;
          if (!pos || !index) return;
          const resource = resources[pos.resourceIndex];
          const event = resource ? eventAtCell(index, resource.id, pos.slotMin) : null;
          if (event) {
            e.preventDefault();
            store.set({
              selectedEventId: event.id,
              focusedEventId: event.id,
              multiSelectedIds: new Set([event.id]),
              range: null,
            });
            onEventSelect?.(event);
          }
          break;
        }
        case "Escape": {
          // Grid-LOCAL staged clear, one stage per press; when nothing is left
          // to clear, bubble WITHOUT consuming so the shared EventShelf chain
          // (pinboard → secondary patient → shelf selection) takes the press.
          // Consuming while handling prevents the double-fire where one press
          // also cleared the pinboard (spec: escapeStagedClear).
          if (state.range || state.multiSelectedIds.size > 0) {
            e.preventDefault();
            e.stopPropagation();
            store.set({ range: null, multiSelectedIds: EMPTY_ID_SET });
          } else if (state.selectedEventId) {
            e.preventDefault();
            e.stopPropagation();
            store.set({ selectedEventId: null });
          } else if (state.focus || state.focusedEventId) {
            e.preventDefault();
            e.stopPropagation();
            store.set({ focus: null, focusedEventId: null });
          }
          break;
        }
        default:
          break;
      }
    },
    [
      cfg,
      store,
      indexRef,
      resourcesRef,
      defaultPos,
      commitFocus,
      moveVertical,
      moveHorizontal,
      extendRange,
      pickUp,
      place,
      cancel,
      nudgeMove,
      onEventSelect,
      onEmptyCellV,
    ],
  );

  return { onKeyDown, commitFocus, applyRange, moveActions };
};
